"use client"
import React, { useCallback, useEffect, useState, useRef } from "react"
import { useLocation } from "react-router"
import { localLogout } from "../utils/logout"
import { getCsrfToken } from "../utils/csrf"
import { verifyLicense } from "@/key"
import { LoginForm } from "./LoginForm"
import WheelNotFound from "../../[wheelId]/not-found"
import { usePushNotifications } from "@/hooks/usePushNotifications"
import { UserData, User } from "@/types/user"

export interface OAuthProvider {
    auth_url: string
    name: string
}

export interface UserContextType extends User {
    loadUser: (forceFromServer?: boolean) => Promise<void>
    updateUser: (updated: User) => void
}

export const UserContext = React.createContext<UserContextType>({
    authorized: false,
    license: '',
    loadUser: async () => { },
    updateUser: () => { },
})

const getUser = async (): Promise<{ user: UserData | null; isNetworkError: boolean }> => {
    try {
        let data = await fetch('/api/auth/profile');
        if (data && data.status == 200) {
            let userData: UserData = await data.json();
            console.log('get user')
            return { user: userData, isNetworkError: false };
        } else if (data && data.status == 404)
            return { user: null, isNetworkError: true };
        return { user: null, isNetworkError: false }; // пользователь не авторизован
    } catch {
        // Ошибка сети - пользователь может быть офлайн
        return { user: null, isNetworkError: true };
    }
}

interface SendMailResponse {
    success: boolean
    message: string
}

const UserProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const location = useLocation();
    const wheelId = location.pathname.split('/')[1] as string | undefined;

    // Push notifications — подключается только для авторизованных пользователей
    const { isSupported, permission, isSubscribed } = usePushNotifications();

    const [user, setUser] = useState<User | null>(null);
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [codeState, setCodeState] = useState(false);
    const [submit, setSubmit] = useState(false);
    const [error, setError] = useState("");
    const [notify, setNotify] = useState("");
    const [oauthProviders, setOauthProviders] = useState<Record<string, OAuthProvider>>({});

    const loadUser = useCallback(async () => {
        console.log('loading local storage user');
        let localUserString = localStorage.getItem('user');

        if (!localUserString) {
            console.log('localstorage user not found');
            setUser({ authorized: false, license: "" });
            setEmail("");
            setCode("");
            setNotify("");
            setError("");
            return;
        }

        let localUser: User | null = null;
        try {
            localUser = JSON.parse(localUserString);
            setUser(localUser);
            console.log('localstorage user loaded:', localUser?.email);
        } catch {
            console.error('Failed to parse local user');
            localLogout();
            return;
        }

        // Проверяем лицензию локальных данных
        if ((localUser as User).license) {
            console.log('checking license expiration from local storage');
            const result = await verifyLicense((localUser as User).license);
            if (result.status === "EXPIRED") {
                console.log('License expired, logging out...');
                localLogout();
            }
        }
    }, []);

    const loadServerUser = useCallback(async () => {
        const { user: serverUser, isNetworkError } = await getUser();
        console.log('get user from server');

        if (isNetworkError) {
            console.log('user load network error - keeping local user for offline mode');
            // При ошибке сети проверяем лицензию локальных данных
            const localUserString = localStorage.getItem('user');
            if (localUserString) {
                try {
                    const localUser: User = JSON.parse(localUserString);
                    if (localUser.license) {
                        console.log('checking license expiration from local storage (network error)');
                        const result = await verifyLicense(localUser.license);
                        if (result.status === "EXPIRED") {
                            console.log('License expired, logging out...');
                            localLogout();
                        }
                    }
                } catch {
                    console.error('Failed to parse local user during network error check');
                }
            }
            return;
        }

        if (serverUser) {
            console.log('user loaded from server');
            const user = {
                ...serverUser,
                authorized: true,
            };
            setUser(user);
            localStorage.setItem('user', JSON.stringify(user));

            // Проверяем лицензию серверных данных
            if (user.license) {
                console.log('checking license expiration from server');
                const result = await verifyLicense(user.license);
                if (result.status === "EXPIRED") {
                    console.log('License expired, logging out...');
                    localLogout();
                    return;
                }
            }
        } else {
            // Сервер ответил unauthorized — разлогиниваемся
            console.log('server says no user - logging out');
            localLogout();
        }
    }, []);

    // Загрузка OAuth провайдеров при монтировании
    useEffect(() => {
        const loadOauthProviders = async () => {
            try {
                const response = await fetch('/api/oauth/oauth/providers');
                if (response.ok) {
                    const data = await response.json();
                    setOauthProviders(data);
                }
            } catch (err) {
                console.error('Failed to load OAuth providers:', err);
            }
        };
        loadOauthProviders();
    }, []);

    // Обработка OAuth callback из URL параметров
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const provider = params.get('provider');

        if (code && provider) {
            const handleOAuthCallback = async () => {
                try {
                    setSubmit(true);
                    setError("");
                    const refKey = localStorage.getItem('ref_key');
                    
                    const response = await fetch('/api/oauth/oauth/callback', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Csrftoken': getCsrfToken(),
                        },
                        body: JSON.stringify({ code, provider, ref_key: refKey || null }),
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.success) {
                            localStorage.removeItem('ref_key');
                            await loadServerUser();
                            // Очищаем URL от параметров
                            window.history.replaceState({}, '', location.pathname);
                        } else {
                            setError(data.message || 'Ошибка OAuth');
                        }
                    } else {
                        setError('Ошибка OAuth авторизации');
                    }
                } catch (err) {
                    console.error('OAuth callback error:', err);
                    setError('Ошибка сети при OAuth авторизации');
                } finally {
                    setSubmit(false);
                }
            };

            handleOAuthCallback();
        }
    }, [location.search, location.pathname, loadServerUser]);

    const allWheels = [
        ...(user?.wheels || []),
        ...(user?.my_clients_wheels || [])
    ];

    const hasWheel = allWheels.some((w) => w.uuid === wheelId) || wheelId === 'my';

    const submitEmail = async () => {
        if (email == "")
            return
        try {
            setError("");
            setSubmit(true);
            let response = await fetch('/api/auth/send-code', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email })
            })
            if (response.status == 200) {
                let responseData: SendMailResponse = await response.json();
                if (responseData.success) {
                    setNotify(responseData.message);
                    setCodeState(true);
                } else {
                    setError(responseData.message)
                }
            } else if (response.status == 429) {
                setError("Лимит запросов. Повторите запрос позднее.")
            } else if (response.status == 422) {
                setError("Введите корректный емайл")
            } else {
                setError("Ошибка. Повторите запрос позднее.")
            }


        } finally {
            setSubmit(false);
        }
    }

    const submitCode = async () => {
        if (email == "")
            return
        try {
            setError("");
            setSubmit(true);
            const refKey = localStorage.getItem('ref_key');
            let response = await fetch('/api/auth/verify-code', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email, code, ref_key: refKey || null })
            })
            if (response.status == 200) {
                let responseData: SendMailResponse = await response.json();
                if (responseData.success) {
                    localStorage.removeItem('ref_key');
                    await loadServerUser();
                } else {
                    setError(responseData.message)
                }
            } else if (response.status == 429) {
                setError("Лимит запросов. Повторите запрос позднее.")
            } else if (response.status == 422) {
                setError("Код не подходит")
            } else {
                setError("Ошибка. Повторите запрос позднее.")
            }


        } finally {
            setSubmit(false);
        }
    }

    console.log('user.authorized=', user?.authorized);
    console.log('hasWheel=', hasWheel);


    const userReady = useRef(false);

    useEffect(() => {
        loadUser().then(() => {
            userReady.current = true;
            // Если пользователь загрузился из localStorage и авторизован — пробуем обновить с сервера
            if (user?.authorized) {
                loadServerUser();
            }
        });
    }, [loadUser, loadServerUser]);

    // Обновляем данные с сервера, когда loadUser завершится и user станет авторизованным
    useEffect(() => {
        if (userReady.current && user?.authorized) {
            loadServerUser();
            userReady.current = false;
        }
    }, [loadServerUser, user?.authorized]);

    // Обновляем пользователя целиком
    const updateUser = useCallback((updated: User) => {
        setUser(updated);
        localStorage.setItem('user', JSON.stringify(updated));
    }, []);

    // Для корневых путей (/ и /my) не делаем ранний возврат
    const isRootPath = !wheelId || wheelId === 'my';

    if (!isRootPath && !hasWheel && user?.authorized) {
        // Показываем 404 для несуществующего wheelId
        return <WheelNotFound />;
    }

    // Если пользователь авторизован (локально или с сервера) — показываем приложение
    if (user && user.authorized) {
        return <UserContext.Provider value={{ ...user, loadUser, updateUser }}>
            {children}
        </UserContext.Provider>
    }

    // Если пользователь ещё не загружен — показываем лоадер
    if (user == null) {
        return null
    }

    // Пользователь не авторизован — показываем форму входа
    return (
        <LoginForm
            email={email}
            setEmail={setEmail}
            code={code}
            setCode={setCode}
            codeState={codeState}
            setCodeState={setCodeState}
            submit={submit}
            setSubmit={setSubmit}
            error={error}
            setError={setError}
            notify={notify}
            setNotify={setNotify}
            submitEmail={submitEmail}
            submitCode={submitCode}
            oauthProviders={oauthProviders}
        />
    )
}
export default UserProvider
